import React, { useState } from 'react';
import cx from 'classnames';
import type { FieldArrayRenderProps } from 'react-final-form-arrays';
import { CheckboxField } from '../CheckboxField';
import { CheckboxListWrapper, Label } from './CheckboxList.styles';
// import './CheckboxList.less';
import { Error } from '../Error';

/**
 * This a component that can be used with react-final-form arrays to render an array of
 * checkboxes.
 *
 * @example
 *    <Form
 *      onSubmit={mockSubmit}
 *      initialValues={{
 *        reasons: [],
 *      }}
 *      mutators={{
 *        ...arrayMutators,
 *      }}
 *      render={({ handleSubmit }) => {
 *        return (
 *          <form onSubmit={handleSubmit}>
 *            <FieldArray name="reasons" options={CHECKBOX_OPTIONS}>
 *              {({ fields, meta }) => (
 *                <CheckboxList fields={fields} meta={meta} options={CHECKBOX_OPTIONS} />
 *              )}
 *            </FieldArray>
 *            <ButtonV2 role="button" type="submit">
 *              Submit
 *            </ButtonV2>
 *          </form>
 *        );
 *      }}
 *    />
 */

/** @see https://github.com/final-form/react-final-form-arrays#fieldarrayrenderprops */
export type CheckboxListProps<FieldValue> = {
  options: {
    label: string | React.ReactNode;
    value: FieldValue;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  }[];
  /** Whether the checkboxes are horizontal or vertical */
  inline?: string;
  /** The label for the checkbox list (at the top) */
  label?: string | React.ReactNode;
  useSelectorStyle?: boolean;
  className?: string;
  listClassName?: string;
  checkboxClassName?: string;
} & FieldArrayRenderProps<FieldValue, HTMLInputElement>;

function CheckboxList<FieldValue>({
  fields,
  options,
  inline,
  label,
  meta,
  useSelectorStyle,
  className,
  checkboxClassName,
  listClassName,
}: CheckboxListProps<FieldValue>): JSX.Element {
  const [touched, setTouched] = useState(false);
  const toggle: (e: React.ChangeEvent<HTMLInputElement>, option: FieldValue) => void = (
    event,
    option
  ) => {
    setTouched(true);
    if (event.target.checked) {
      fields.push(option);
    } else {
      fields.remove(fields.value.indexOf(option));
    }
  };

  const errorMessage = meta && (touched || meta.submitFailed) && meta.error;
  return (
    <CheckboxListWrapper
      className={cx('checkbox-list-wrapper', className, { error: errorMessage })}
      data-testid="checkbox-list-wrapper"
    >
      {label && <Label className="label">{label}</Label>}
      <div className={cx(listClassName, { inline })}>
        {options.map((opt, index) => {
          const { value, label: checkboxLabel, onChange, ...rest } = opt;
          return (
            <CheckboxField
              className={cx('checkbox-list-item', checkboxClassName)}
              key={index}
              id={String(value)}
              onChange={(event) => {
                toggle(event, value);
                if (onChange) {
                  onChange(event);
                }
              }}
              label={checkboxLabel}
              checked={!!fields.value && fields.value.includes(value)}
              useSelectorStyle={useSelectorStyle}
              {...rest}
            />
          );
        })}
      </div>
      {errorMessage && <Error message={errorMessage} />}
    </CheckboxListWrapper>
  );
}

export default CheckboxList;
